import React from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MdUploadFile, MdClose } from 'react-icons/md';
import { useDeviceDetection } from '../../hooks/useDeviceDetection';

interface FileUploadDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: () => void;
  onDropzoneClick: () => void;
  uploadedFiles: Array<{ name: string; status?: string; type?: string }>;
  isUploading: boolean;
  isDataAnalysisMode?: boolean;
  onRemoveFile?: (fileName: string) => void;
}

export function FileUploadDialog({ 
  isOpen, 
  onClose, 
  onUpload, 
  onDropzoneClick,
  uploadedFiles,
  isUploading,
  isDataAnalysisMode = false,
  onRemoveFile
}: FileUploadDialogProps) {
  const { isMobile } = useDeviceDetection();
  const [canClick, setCanClick] = React.useState(true); // Start with true
  const [isFilePickerOpen, setIsFilePickerOpen] = React.useState(false);
  const [showUploadMessage, setShowUploadMessage] = React.useState(false);
  const [isDragging, setIsDragging] = React.useState(false);

  // Prevent immediate clicks when dialog opens (especially on mobile where touch events can bubble)
  React.useEffect(() => {
    if (isOpen) {
      // Don't block clicks anymore - the issue was affecting react-select
      setCanClick(true);
    }
  }, [isOpen]);

  // Reset file picker state when dialog closes
  React.useEffect(() => {
    if (!isOpen) {
      setIsFilePickerOpen(false);
      setShowUploadMessage(false);
    }
  }, [isOpen]);

  // Detect when file picker is dismissed (user comes back to dialog without selecting files)
  React.useEffect(() => {
    if (isFilePickerOpen) {
      const handleFocus = () => {
        // Small delay to ensure any file selection is processed first
        setTimeout(() => {
          setIsFilePickerOpen(false);
          setShowUploadMessage(true);
          // Hide message after 3 seconds
          setTimeout(() => setShowUploadMessage(false), 5000);
        }, 100);
      };

      window.addEventListener('focus', handleFocus);
      return () => window.removeEventListener('focus', handleFocus);
    }
  }, [isFilePickerOpen]);

  // Check if files are ready - for staged files (general chat), they're ready immediately
  // For data analysis mode, files are ready when they have 'pending' status (ready to be uploaded)
  // or 'uploaded' status (already uploaded). They're NOT ready if 'uploading' or 'error'.
  const allFilesReady = uploadedFiles.length > 0 && 
    (isDataAnalysisMode 
      ? uploadedFiles.every(f => f.status === 'pending' || f.status === 'uploaded')
      : uploadedFiles.every(f => f.status !== 'uploading')
    );
  const hasFiles = uploadedFiles.length > 0;

  // Detect file type category from uploaded files
  const getFileTypeCategory = () => {
    if (uploadedFiles.length === 0) return null;
    
    const firstFile = uploadedFiles[0];
    const isImage = /\.(png|jpe?g)$/i.test(firstFile.name) || /image\/(png|jpeg)/.test(firstFile.type || '');
    const isData = /\.(csv|xlsx?|xlsm)$/i.test(firstFile.name) || 
                   ['text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'].includes(firstFile.type || '');
    const isText = /\.(pdf|docx?|pptx?|txt)$/i.test(firstFile.name) || 
                   ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                    'application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation'].includes(firstFile.type || '');
    
    if (isImage) return 'image';
    if (isData) return 'data';
    if (isText) return 'text';
    return null;
  };

  const fileTypeCategory = getFileTypeCategory();

  const handleUploadClick = () => {
    onUpload();
    // Don't call onClose() here - the upload handler will close the dialog
    // This way we can distinguish between successful upload and cancellation
  };

  const handleCancelClick = () => {
    // Cancel button can be pressed anytime - parent will handle cleanup
    onClose();
  };

  const handleDropzoneClick = () => {
    // Only allow clicks after the dialog has been open for a moment (prevents touch event bubbling on mobile)
    if (canClick) {
      setIsFilePickerOpen(true);
      onDropzoneClick();
    }
  };

  // Drag and drop handlers
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set dragging to false if we're leaving the dropzone itself, not a child element
    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      // Trigger the file input change event with the dropped files
      // We need to programmatically set the files on the hidden input
      // and trigger its change event
      const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
      if (fileInput) {
        // Create a new DataTransfer object and add files
        const dataTransfer = new DataTransfer();
        Array.from(files).forEach(file => dataTransfer.items.add(file));
        fileInput.files = dataTransfer.files;
        
        // Trigger change event
        const event = new Event('change', { bubbles: true });
        fileInput.dispatchEvent(event);
      }
    }
  };

  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 }
  };

  const dialogVariants = isMobile
    ? {
        hidden: { y: '100%' },
        visible: { y: 0 }
      }
    : {
        hidden: { scale: 0.9 },
        visible: { scale: 1 }
      };

  const dialogContent = (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 z-[10000]"
            variants={backdropVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            onClick={(e) => {
              // Don't close if clicking on react-select menu
              if ((e.target as HTMLElement).closest('.select__menu')) {
                return;
              }
              handleCancelClick();
            }}
          />

          {/* Dialog - Mobile: Full screen, Desktop: Centered with flexbox */}
          {isMobile ? (
            <motion.div
              className="fixed inset-0 z-[10001] bg-[var(--color-bg)]"
              style={{ backgroundColor: 'var(--color-bg)', opacity: 1 }}
              variants={dialogVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              transition={{ duration: 0.2, ease: 'easeOut' }}
            >
              {/* Close button for mobile */}
              <div className="flex justify-end p-4">
                <button
                  onClick={handleCancelClick}
                  className="p-2 rounded-full hover:bg-[var(--hover)] transition-colors"
                  aria-label="Close"
                >
                  {React.createElement(MdClose as any, { className: "w-6 h-6 text-[var(--color-text)]" })}
                </button>
              </div>

              {/* Content */}
              <div className="p-6 pt-2">
                {/* Drop Zone */}
                <div
                  onClick={handleDropzoneClick}
                  onDragEnter={handleDragEnter}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  className={`border-2 border-dashed rounded-lg p-8 mb-6 cursor-pointer transition-all ${
                    isDragging 
                      ? 'border-[var(--color-accent)] bg-[var(--color-accent)] bg-opacity-10' 
                      : 'border-[var(--border)] hover:border-[var(--color-accent)] hover:bg-[var(--hover)]'
                  }`}
                >
                  <div className="flex flex-col items-center justify-center gap-3">
                    {isFilePickerOpen ? (
                      <div className="w-12 h-12 border-4 border-[var(--color-text)] border-t-transparent rounded-full animate-spin" />
                    ) : (
                      React.createElement(MdUploadFile as any, { className: "w-12 h-12 text-[var(--color-text)]" })
                    )}
                    <p className="font-bold text-[var(--color-text)] text-center">
                     Select files
                    </p>
                  </div>
                </div>

                {/* Upload processing message */}
                {showUploadMessage && (
                  <div className="mb-4 p-3 bg-blue-500 bg-opacity-10 border border-blue-500 rounded-lg">
                    <p className="text-sm text-[var(--color-text)]">
                      Any uploaded files may take a second to finish uploading. Please be patient for large files.
                    </p>
                  </div>
                )}

                {/* Data Analysis requirement note */}
                {isDataAnalysisMode && (
                  <div className="mb-4 p-3 bg-blue-500 bg-opacity-10 border border-blue-500 rounded-lg">
                    <p className="text-sm text-[var(--color-text)]">
                      <span className="font-semibold">Note:</span> The data analysis tool requires files to be attached. Please select your data files to continue.
                    </p>
                  </div>
                )}

                {/* Important Note about file type mixing */}
                {!fileTypeCategory && !isDataAnalysisMode && (
                  <div className="mb-4 p-3 bg-yellow-100 dark:bg-yellow-900 dark:bg-opacity-20 border border-yellow-600 dark:border-yellow-500 rounded-lg">
                    <p className="text-sm text-gray-800 dark:text-[var(--color-text)]">
                      <span className="font-semibold">Note:</span> You cannot mix file types. Choose all text documents, all images, or all data files.
                    </p>
                  </div>
                )}

                {/* File Type Information - Show only the relevant file type */}
                <div className="mb-6 text-sm text-[var(--color-text)]">
                  {!fileTypeCategory && !isDataAnalysisMode && (
                    <div className="space-y-3">
                      <div className="flex gap-2 items-start">
                        <span className="flex-shrink-0">•</span>
                        <div>
                          <span className="font-medium">Text documents</span>
                          <span className="opacity-70"> (.docx, .txt, .pdf, .pptx)</span>
                          <span>: Up to 2 files, no more than 16MB per file. ~200 pages max</span>
                        </div>
                      </div>
                      <div className="flex gap-2 items-start">
                        <span className="flex-shrink-0">•</span>
                        <div>
                          <span className="font-medium">Images</span>
                          <span className="opacity-70"> (.jpeg, .jpg, .png)</span>
                          <span>: Up to 5 files at a time, 5MB each</span>
                        </div>
                      </div>
                      <div className="flex gap-2 items-start">
                        <span className="flex-shrink-0">•</span>
                        <div>
                          <span className="font-medium">Data Files</span>
                          <span className="opacity-70"> (.csv, .xlsx)</span>
                          <span>: Up to 2 files, no more than 16MB each</span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {fileTypeCategory === 'text' && (
                    <div>
                      <span className="font-medium">Text documents</span>
                      <span className="opacity-70"> (.docx, .txt, .pdf, .pptx)</span>
                      <span>: Up to 2 files, no more than 16MB per file. ~200 pages max</span>
                    </div>
                  )}
                  
                  {fileTypeCategory === 'image' && (
                    <div>
                      <span className="font-medium">Images</span>
                      <span className="opacity-70"> (.jpeg, .jpg, .png)</span>
                      <span>: Up to 5 files at a time, 5MB each</span>
                    </div>
                  )}
                  
                  {(fileTypeCategory === 'data' || isDataAnalysisMode) && (
                    <div>
                      <span className="font-medium">Data Files</span>
                      <span className="opacity-70"> (.csv, .xlsx)</span>
                      <span>: Up to 2 files, no more than 16MB each</span>
                    </div>
                  )}
                </div>

                {/* File Previews - Show uploaded files */}
                {hasFiles && (
                  <div className="mb-6 p-4 border-2 border-[var(--color-accent)] rounded-lg bg-[var(--color-bg)] bg-opacity-5">
                    <h3 className="text-sm font-semibold text-[var(--color-text)] mb-2">Selected Files:</h3>
                    <div className="space-y-2">
                      {uploadedFiles.map((file, index) => (
                        <div
                          key={`${file.name}-${index}`}
                          className="flex items-center gap-2 p-2 bg-[var(--hover)] rounded-lg"
                        >
                          <span className="flex-1 text-sm text-[var(--color-text)] truncate">
                            {file.name}
                          </span>
                          {file.status === 'uploading' && (
                            <span className="text-xs text-[var(--color-accent)]">Uploading...</span>
                          )}
                          {onRemoveFile && (
                            <button
                              onClick={() => onRemoveFile(file.name)}
                              className="p-1 hover:bg-[var(--border)] rounded transition-colors"
                              aria-label={`Remove ${file.name}`}
                            >
                              {React.createElement(MdClose as any, { className: "w-4 h-4 text-[var(--color-text)]" })}
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={handleCancelClick}
                    className="px-4 py-2 rounded-lg border border-[var(--border)] text-[var(--color-text)] hover:bg-[var(--hover)] transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUploadClick}
                    disabled={!allFilesReady || !hasFiles}
                    className="px-4 py-2 rounded-lg bg-[var(--color-accent)] text-white hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isUploading ? 'Uploading...' : 'Upload File(s)'}
                  </button>
                </div>
              </div>
            </motion.div>
          ) : (
            <div className="fixed inset-0 z-[10001] flex items-center justify-center pointer-events-none">
              <motion.div
                className="bg-[var(--color-bg)] rounded-lg shadow-xl border border-[var(--border)] w-[90vw] max-w-[500px] pointer-events-auto"
                style={{ backgroundColor: 'var(--color-bg)', opacity: 1 }}
                variants={dialogVariants}
                initial="hidden"
                animate="visible"
                exit="hidden"
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                {/* Content */}
                <div className="p-6">
                  {/* Drop Zone */}
                  <div
                    onClick={handleDropzoneClick}
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={`border-2 border-dashed rounded-lg p-8 mb-6 cursor-pointer transition-all ${
                      isDragging 
                        ? 'border-[var(--color-accent)] bg-[var(--color-accent)] bg-opacity-10' 
                        : 'border-[var(--border)] hover:border-[var(--color-accent)] hover:bg-[var(--hover)]'
                    }`}
                  >
                    <div className="flex flex-col items-center justify-center gap-3">
                    {isFilePickerOpen ? (
                      <div className="w-12 h-12 border-4 border-[var(--color-text)] border-t-transparent rounded-full animate-spin" />
                    ) : (
                      React.createElement(MdUploadFile as any, { className: "w-12 h-12 text-[var(--color-text)]" })
                    )}
                      <p className="font-bold text-[var(--color-text)] text-center">
                        Drag and drop or select files
                      </p>
                    </div>
                  </div>

                  {/* Upload processing message */}
                  {showUploadMessage && (
                    <div className="mb-4 p-3 bg-blue-500 bg-opacity-10 border border-blue-500 rounded-lg">
                      <p className="text-sm text-[var(--color-text)]">
                      Any uploaded files may take a second to finish uploading. Please be patient for large files.
                      </p>
                    </div>
                  )}

                  {/* Data Analysis requirement note */}
                  {(isDataAnalysisMode) && (
                    <div className="mb-4 p-3 bg-blue-500 bg-opacity-10 border border-blue-500 rounded-lg">
                      <p className="text-sm text-[var(--color-text)]">
                        <span className="font-semibold">Note:</span> The data analysis tool requires files to be attached. Please select your data files to continue.
                      </p>
                    </div>
                  )}


                  {/* Important Note about file type mixing */}
                  {!fileTypeCategory && !isDataAnalysisMode && (
                    <div className="mb-4 p-3 bg-yellow-100 dark:bg-yellow-900 dark:bg-opacity-20 border border-yellow-600 dark:border-yellow-500 rounded-lg">
                      <p className="text-sm text-gray-800 dark:text-[var(--color-text)]">
                        <span className="font-semibold">Note:</span> You cannot mix file types. Choose all text documents, all images, or all data files.
                      </p>
                    </div>
                  )}

                  {/* File Type Information */}
                  <div className="mb-6 text-sm text-[var(--color-text)]">
                    {!fileTypeCategory && !isDataAnalysisMode && (
                      <div className="space-y-3">
                        <div className="flex gap-2 items-start">
                          <span className="flex-shrink-0">•</span>
                          <div>
                            <span className="font-medium">Text documents</span>
                            <span className="opacity-70"> (.docx, .txt, .pdf, .pptx)</span>
                            <span>: Up to 2 files, no more than 16MB per file. ~200 pages max</span>
                          </div>
                        </div>
                        <div className="flex gap-2 items-start">
                          <span className="flex-shrink-0">•</span>
                          <div>
                            <span className="font-medium">Images</span>
                            <span className="opacity-70"> (.jpeg, .jpg, .png)</span>
                            <span>: Up to 5 files at a time, 5MB each</span>
                          </div>
                        </div>
                        <div className="flex gap-2 items-start">
                          <span className="flex-shrink-0">•</span>
                          <div>
                            <span className="font-medium">Data Files</span>
                            <span className="opacity-70"> (.csv, .xlsx)</span>
                            <span>: Up to 2 files, no more than 16MB each</span>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {fileTypeCategory === 'text' && (
                      <div>
                        <span className="font-medium">Text documents</span>
                        <span className="opacity-70"> (.docx, .txt, .pdf, .pptx)</span>
                        <span>: Up to 2 files, no more than 16MB per file. ~200 pages max</span>
                      </div>
                    )}
                    
                    {fileTypeCategory === 'image' && (
                      <div>
                        <span className="font-medium">Images</span>
                        <span className="opacity-70"> (.jpeg, .jpg, .png)</span>
                        <span>: Up to 5 files at a time, 5MB each</span>
                      </div>
                    )}
                    
                    {(fileTypeCategory === 'data' || isDataAnalysisMode) && (
                      <div>
                        <span className="font-medium">Data Files</span>
                        <span className="opacity-70"> (.csv, .xlsx)</span>
                        <span>: Up to 2 files, no more than 16MB each</span>
                      </div>
                    )}
                  </div>

                  {/* File Previews - Show uploaded files */}
                  {hasFiles && (
                    <div className="mb-6 p-4 border-2 border-[var(--color-accent)] rounded-lg bg-[var(--color-bg)] bg-opacity-5">
                      <h3 className="text-sm font-semibold text-[var(--color-text)] mb-2">Selected Files:</h3>
                      <div className="space-y-2">
                        {uploadedFiles.map((file, index) => (
                          <div
                            key={`${file.name}-${index}`}
                            className="flex items-center gap-2 p-2 bg-[var(--hover)] rounded-lg"
                          >
                            <span className="flex-1 text-sm text-[var(--color-text)] truncate">
                              {file.name}
                            </span>
                            {file.status === 'uploading' && (
                              <span className="text-xs text-[var(--color-accent)]">Uploading...</span>
                            )}
                            {onRemoveFile && (
                              <button
                                onClick={() => onRemoveFile(file.name)}
                                className="p-1 hover:bg-[var(--border)] rounded transition-colors"
                                aria-label={`Remove ${file.name}`}
                              >
                                {React.createElement(MdClose as any, { className: "w-4 h-4 text-[var(--color-text)]" })}
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-3 justify-end">
                    <button
                      onClick={handleCancelClick}
                      className="px-4 py-2 rounded-lg border border-[var(--border)] text-[var(--color-text)] hover:bg-[var(--hover)] transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleUploadClick}
                      disabled={!allFilesReady || !hasFiles}
                      className="px-4 py-2 rounded-lg bg-[var(--color-accent)] text-white hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isUploading ? 'Uploading...' : 'Upload File(s)'}
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          )}
        </>
      )}
    </AnimatePresence>
  );

  // Render in a portal to escape any parent stacking contexts
  return createPortal(dialogContent, document.body);
}
